import type { Classroom } from "@classroom/domain";

export type ClassSetupDraft = {
  homeroom: string;
  teaching: Array<{ className: string; subject: string }>;
};

export function normalizeClassName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLocaleUpperCase();
}

export function setupIssue(draft: ClassSetupDraft): "empty" | "incomplete" | "duplicate" | null {
  const rows = draft.teaching.filter((row) => row.className.trim() || row.subject.trim());
  if (!draft.homeroom.trim() && !rows.length) return "empty";
  const pairs = new Set<string>();
  for (const row of rows) {
    if (!row.className.trim() || !row.subject.trim()) return "incomplete";
    const key = JSON.stringify([normalizeClassName(row.className), row.subject.trim().toLocaleLowerCase()]);
    if (pairs.has(key)) return "duplicate";
    pairs.add(key);
  }
  return null;
}

export function teachingSetupIssue(draft: ClassSetupDraft, noTeaching: boolean) {
  return (
    setupIssue(draft) ||
    (!noTeaching && !draft.teaching.some((row) => row.className.trim() || row.subject.trim()) ? "teachingChoice" : null)
  );
}

// Existing records keep their IDs and roster links; setup never merges or deletes rosters.
export function buildSetupClasses(
  draft: ClassSetupDraft,
  workspaceId: string,
  existing: Classroom[] = [],
): Classroom[] {
  const issue = setupIssue(draft);
  if (issue) throw new Error(issue);
  const now = new Date().toISOString();
  const active = existing.filter((item) => item.workspaceId === workspaceId && item.active);
  const existingHome = active.filter((item) => item.classRole === "homeroom");
  if (
    draft.homeroom &&
    existingHome.some(
      (item) => normalizeClassName(item.name).replace(/ 班主任$/, "") !== normalizeClassName(draft.homeroom),
    )
  )
    throw new Error("existingHomeroom");
  const sources = new Map<string, string>();
  const classes: Classroom[] = [];
  const add = (className: string, classRole: Classroom["classRole"], subject = "") => {
    const code = normalizeClassName(className);
    const name = subject ? `${code} ${subject.trim()}` : code;
    const matches = active.filter(
      (item) =>
        item.classRole === classRole &&
        normalizeClassName(item.name).replace(classRole === "homeroom" ? / 班主任$/ : /$^/, "") ===
          normalizeClassName(name),
    );
    if (matches.length > 1) throw new Error("ambiguous");
    const previous = matches[0];
    if (previous) {
      const sourceId = previous.sharedDataClassId || previous.id;
      if (!sources.has(code)) sources.set(code, sourceId);
      classes.push(previous.name === name ? previous : { ...previous, name, updatedAt: now });
      return;
    }
    if (!sources.has(code)) {
      const roots = active.filter(
        (item) => !item.sharedDataClassId && normalizeClassName(item.name.split(/\s+/)[0] || "") === code,
      );
      if (roots.length > 1) throw new Error("ambiguous");
      if (roots[0]) sources.set(code, roots[0].id);
    }
    const item: Classroom = {
      id: `class_${crypto.randomUUID()}`,
      workspaceId,
      name,
      classRole,
      active: true,
      sharedDataClassId: sources.get(code),
      createdAt: now,
      updatedAt: now,
    };
    if (!sources.has(code)) sources.set(code, item.id);
    classes.push(item);
  };
  if (draft.homeroom.trim()) add(draft.homeroom, "homeroom");
  for (const row of draft.teaching) {
    if (row.className.trim() || row.subject.trim()) add(row.className, "teaching", row.subject);
  }
  return classes;
}

export function existingSetupDraft(classes: Classroom[]): ClassSetupDraft {
  const active = classes.filter((item) => item.active);
  return {
    homeroom: active.find((item) => item.classRole === "homeroom")?.name.replace(/ 班主任$/, "") || "",
    teaching: active
      .filter((item) => item.classRole === "teaching")
      .map((item) => {
        const [className = "", ...subject] = item.name.trim().split(/\s+/);
        return { className: normalizeClassName(className), subject: subject.join(" ") };
      }),
  };
}
